import { create } from 'zustand';
import { User } from '../types';
import { supabase } from '../lib/supabase';
import { AuthError, AuthApiError, PostgrestError } from '@supabase/supabase-js';
import { logger } from '../lib/logger';

interface AuthState {
  user: User | null;
  loading: boolean;
  logs: string[];
  error: string | null;
  setUser: (user: User | null) => void;
  setError: (error: string | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, businessDetails: any) => Promise<void>;
  signOut: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  clearLogs: () => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;
const MAX_LOGS = 100;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const addLog = (message: string, data?: any) => {
  const timestamp = new Date().toISOString();
  return `[${timestamp}] ${message}${data ? '\n' + JSON.stringify(data, null, 2) : ''}`;
};

const isServerError = (error: any): boolean => {
  return (
    error?.status >= 500 ||
    error?.message?.includes('Database error') ||
    error?.message?.includes('unexpected_failure') ||
    error?.code === 'unexpected_failure'
  );
};

const getErrorMessage = (error: any): string => {
  if (isServerError(error)) {
    return 'Our authentication service is temporarily unavailable. Please try again in a few moments.';
  }
  
  if (error instanceof AuthApiError) {
    if (error.status === 400) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (error.status === 422) {
      return 'Please check your email and password format.';
    }
    if (error.status === 429) {
      return 'Too many login attempts. Please wait a moment before trying again.';
    }
  }
  
  return error?.message || 'An unexpected error occurred. Please try again.';
};

const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = MAX_RETRIES,
  baseDelay: number = RETRY_DELAY
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry client errors (4xx) except for 429 (rate limit)
      if (error instanceof AuthApiError && error.status >= 400 && error.status < 500 && error.status !== 429) {
        throw error;
      }
      
      // Don't retry on the last attempt
      if (attempt === maxRetries - 1) {
        throw error;
      }
      
      // Only retry server errors or rate limits
      if (isServerError(error) || error?.status === 429) {
        const delay = baseDelay * Math.pow(2, attempt);
        await sleep(delay);
      } else {
        throw error;
      }
    }
  }
  
  throw lastError;
};
export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  logs: [],
  error: null,
  setUser: (user) => set({ user }),
  setError: (error) => set({ error }),
  clearLogs: () => set({ logs: [] }),

  initializeAuth: async () => {
    const log = (message: string, data?: any) => set(state => ({
      logs: [addLog(message, data), ...state.logs].slice(0, MAX_LOGS)
    }));

    try {
      console.log('[AuthStore] Starting auth initialization...');
      set({ error: null });
      log('Starting auth initialization');

      console.log('[AuthStore] Fetching session...');

      // Add timeout to prevent hanging
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(() => resolve({ data: { session: null }, error: null }), 5000)
      );

      const { data: { session }, error: sessionError } = await Promise.race([
        sessionPromise,
        timeoutPromise
      ]) as any;

      console.log('[AuthStore] Session result:', { session: !!session, error: sessionError });

      if (sessionError) {
        console.error('[AuthStore] Session error:', sessionError);
        log('Session error', sessionError);
        set({ user: null, loading: false, error: getErrorMessage(sessionError) });
        return;
      }

      if (!session?.user) {
        console.log('[AuthStore] No active session found');
        log('No active session');
        set({ user: null, loading: false });
        return;
      }

      console.log('[AuthStore] Session found for user:', session.user.id);
      log('Session found', { userId: session.user.id });

      // Fetch profile
      console.log('[AuthStore] Fetching profile...');
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, subscription_status')
        .eq('user_id', session.user.id)
        .maybeSingle();
      console.log('[AuthStore] Profile result:', { profile, error: profileError });

      if (profileError) {
        console.error('[AuthStore] Profile fetch error:', profileError);
        log('Profile fetch error', profileError);
        set({
          user: null,
          loading: false,
          error: 'Unable to load user profile: ' + profileError.message
        });
        return;
      }

      if (!profile) {
        console.warn('[AuthStore] No profile found for user, using default role');
        log('Profile not found, using defaults');
        set({
          user: {
            id: session.user.id,
            email: session.user.email!,
            role: 'client',
            subscription_status: 'active'
          },
          loading: false,
          error: null
        });
        return;
      }

      console.log('[AuthStore] Profile loaded successfully:', profile);
      console.log('[AuthStore] Setting user state...');
      set({
        user: {
          id: session.user.id,
          email: session.user.email!,
          role: profile.role,
          subscription_status: profile.subscription_status
        },
        loading: false,
        error: null
      });

      console.log('[AuthStore] Auth initialization completed successfully');
      log('Auth initialization completed');
    } catch (error) {
      console.error('[AuthStore] Auth initialization failed:', error);
      log('Auth initialization failed', error);
      set({
        user: null,
        loading: false,
        error: getErrorMessage(error)
      });
    }
  },

  signIn: async (email, password) => {
    const log = (message: string, data?: any) => set(state => ({
      logs: [addLog(message, data), ...state.logs].slice(0, MAX_LOGS)
    }));

    try {
      set({ error: null, loading: false });
      log('Starting sign in', { email });

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      if (!data.user) throw new Error('No user returned from auth service');

      log('Sign in successful', { userId: data.user.id });
      logger.logAuthEvent('sign_in_success', true, { userId: data.user.id, email });

      // Wait for profile
      await sleep(500);

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, subscription_status')
        .eq('user_id', data.user.id)
        .maybeSingle();

      if (profileError) {
        log('Profile error', profileError);
        logger.error('AUTH', 'Failed to fetch user profile', { error: profileError.message, userId: data.user.id });
        throw new Error('Unable to fetch user profile: ' + profileError.message);
      }

      if (!profile) {
        log('No profile found for user');
        logger.warning('AUTH', 'No profile found for authenticated user', { userId: data.user.id });
        throw new Error('No profile found. Please contact support.');
      }

      set({
        user: {
          id: data.user.id,
          email: data.user.email!,
          role: profile.role,
          subscription_status: profile.subscription_status
        },
        loading: false,
        error: null
      });

      log('Sign in completed');
      logger.info('AUTH', 'User signed in successfully', { userId: data.user.id, role: profile.role });
    } catch (error) {
      log('Sign in failed', error);
      logger.logAuthEvent('sign_in_failed', false, { email, error: getErrorMessage(error) });
      const errorMessage = getErrorMessage(error);
      set({ error: errorMessage, loading: false });
      throw new Error(errorMessage);
    }
  },

  signUp: async (email, password, businessDetails) => {
    const log = (message: string, data?: any) => set(state => ({
      logs: [addLog(message, data), ...state.logs].slice(0, MAX_LOGS)
    }));

    try {
      set({ error: null });
      log('Starting sign up', { email });
      await supabase.auth.signOut();

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: 'client'
          }
        }
      });

      if (error) {
        if (error instanceof AuthApiError && error.status === 400) {
          if (error.message.toLowerCase().includes('already exists')) {
            throw new Error('An account with this email already exists');
          }
        }
        throw error;
      }

      if (!data.user) throw new Error('No user returned from auth service');

      log('User created', { userId: data.user.id });

      // Wait for profile creation trigger to complete
      await sleep(1500);

      // Create client record
      const { error: clientError } = await supabase
        .from('clients')
        .insert([{
          user_id: data.user.id,
          company_name: businessDetails.companyName,
          email: email,
          phone: businessDetails.phone || null
        }]);

      if (clientError) {
        log('Client creation failed', clientError);
        throw new Error('Failed to create client record: ' + clientError.message);
      }

      set({ error: null });
      log('Sign up completed successfully');
    } catch (error) {
      log('Sign up failed', error);
      const errorMessage = getErrorMessage(error);
      set({ error: errorMessage });
      throw error;
    }
  },

  signOut: async () => {
    const log = (message: string, data?: any) => set(state => ({
      logs: [addLog(message, data), ...state.logs].slice(0, MAX_LOGS)
    }));

    try {
      set({ error: null });
      log('Starting sign out');
      const { error } = await retryWithBackoff(
        () => supabase.auth.signOut()
      );
      if (error) throw error;
      log('Sign out successful');
      set({ user: null, error: null });
    } catch (error) {
      log('Sign out failed', error);
      set({ user: null, error: getErrorMessage(error) });
      throw error;
    }
  }
}));

// Auth state listener
supabase.auth.onAuthStateChange(async (event, session) => {
  const log = (message: string, data?: any) => useAuthStore.setState(state => ({
    logs: [addLog(message, data), ...state.logs].slice(0, MAX_LOGS)
  }));

  log('Auth state changed', { event, userId: session?.user?.id });

  if (event === 'SIGNED_IN' && session?.user) {
    try {
      await sleep(500);

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, subscription_status')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (profileError) {
        log('Profile fetch error', profileError);
        useAuthStore.setState({
          user: null,
          loading: false,
          error: 'Unable to load user profile: ' + profileError.message
        });
        return;
      }

      if (!profile) {
        log('No profile found');
        useAuthStore.setState({
          user: null,
          loading: false,
          error: 'Profile not found. Please contact support.'
        });
        return;
      }

      useAuthStore.setState({
        user: {
          id: session.user.id,
          email: session.user.email!,
          role: profile.role,
          subscription_status: profile.subscription_status
        },
        loading: false,
        error: null
      });
    } catch (error) {
      log('Auth state change error', error);
      useAuthStore.setState({
        user: null,
        loading: false,
        error: getErrorMessage(error)
      });
    }
  } else if (event === 'SIGNED_OUT') {
    log('User signed out');
    useAuthStore.setState({ user: null, loading: false, error: null });
  }
});